import { useEffect, useMemo, useState } from 'react';
import { loadPuzzles } from '../../lib/puzzles/load';
import { pickPuzzles } from '../../lib/puzzles/select';
import { estimatePuzzleRating, initialPuzzleRating } from '../../lib/puzzles/rating';
import { puzzleThemeLabel, puzzleThemesForWeakness } from '../../lib/puzzles/themes';
import { PuzzlePlayer } from './PuzzlePlayer';
import type { CoachingReport, PuzzleAttempt, StoredGame, Theme } from '../../types';
import type { Puzzle } from '../../types';

export interface PuzzlesTabProps {
  games: StoredGame[];
  reports: Record<string, CoachingReport>;
  attempts: PuzzleAttempt[];
  onAttempt: (attempt: PuzzleAttempt) => void;
  /** Demande externe (ex. « S'entraîner sur ce thème » depuis Ma progression) : démarre une série sur ces thèmes dès que la banque est chargée. */
  requestedThemes?: string[];
  onThemesConsumed?: () => void;
}

const SESSION_SIZE = 5;

function latestChessComRating(games: StoredGame[]): number | null {
  const withRating = games.filter((g) => g.chessCom?.playerRating != null && g.chessCom.timeClass !== 'bullet');
  if (withRating.length === 0) return null;
  const sorted = [...withRating].sort((a, b) => (b.chessCom!.endTime ?? 0) - (a.chessCom!.endTime ?? 0));
  return sorted[0].chessCom!.playerRating ?? null;
}

/** Thèmes de gaffes les plus fréquents parmi les moments confirmés en partie, du plus au moins fréquent. */
function weakestThemesFromGames(reports: Record<string, CoachingReport>): Theme[] {
  const counts = new Map<Theme, number>();
  for (const r of Object.values(reports)) {
    for (const m of r.momentsForExercises) {
      for (const t of m.themes) counts.set(t, (counts.get(t) ?? 0) + 1);
    }
  }
  return [...counts.entries()].sort((a, b) => b[1] - a[1]).map(([t]) => t);
}

export function PuzzlesTab({ games, reports, attempts, onAttempt, requestedThemes, onThemesConsumed }: PuzzlesTabProps) {
  const [pool, setPool] = useState<Puzzle[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [session, setSession] = useState<Puzzle[] | null>(null);
  const [sessionIndex, setSessionIndex] = useState(0);
  const [sessionKey, setSessionKey] = useState(0);

  useEffect(() => {
    loadPuzzles()
      .then(setPool)
      .catch((err) => setLoadError(err instanceof Error ? err.message : 'Chargement de la banque de puzzles impossible.'));
  }, []);

  const rating = useMemo(
    () => estimatePuzzleRating(attempts, initialPuzzleRating(latestChessComRating(games))),
    [attempts, games],
  );

  const weakThemes = useMemo(() => weakestThemesFromGames(reports), [reports]);
  const suggestedPuzzleThemes = useMemo(() => puzzleThemesForWeakness(weakThemes.slice(0, 2)), [weakThemes]);
  const primaryWeakness = weakThemes[0];

  const themeStats = useMemo(() => {
    const byTheme = new Map<string, { total: number; solved: number }>();
    for (const a of attempts) {
      for (const t of a.themes) {
        const s = byTheme.get(t) ?? { total: 0, solved: 0 };
        s.total += 1;
        if (a.solvedFirstTry) s.solved += 1;
        byTheme.set(t, s);
      }
    }
    return [...byTheme.entries()]
      .filter(([t]) => puzzleThemeLabel(t) !== t) // ne garde que nos thèmes ciblés (avec libellé FR)
      .map(([theme, s]) => ({ theme, ...s }))
      .sort((a, b) => b.total - a.total);
  }, [attempts]);

  function startSession(themes?: string[]) {
    if (!pool) return;
    const attemptedIds = new Set(attempts.map((a) => a.puzzleId));
    let picks = pickPuzzles({ pool, themes, targetRating: rating, excludeIds: attemptedIds, count: SESSION_SIZE });
    if (picks.length < SESSION_SIZE) {
      // Pas assez de puzzles jamais tentés sur ce thème : on complète en
      // acceptant d'en retenter (mieux qu'une série plus courte que prévu).
      picks = pickPuzzles({ pool, themes, targetRating: rating, count: SESSION_SIZE });
    }
    setSession(picks);
    setSessionIndex(0);
    setSessionKey((k) => k + 1);
  }

  function handlePuzzleCompleted(attempt: PuzzleAttempt) {
    onAttempt(attempt);
  }

  useEffect(() => {
    if (!pool || !requestedThemes || requestedThemes.length === 0) return;
    startSession(requestedThemes);
    onThemesConsumed?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pool, requestedThemes]);

  if (loadError) {
    return (
      <section className="panel">
        <h2>Puzzles</h2>
        <p className="error-block" role="alert">
          {loadError}
        </p>
      </section>
    );
  }

  if (session) {
    const puzzle = session[sessionIndex];
    const isLast = sessionIndex === session.length - 1;
    return (
      <section className="panel">
        <button type="button" className="link-btn" onClick={() => setSession(null)}>
          ← Retour
        </button>
        <h2>
          Puzzle {sessionIndex + 1} / {session.length}
        </h2>
        <PuzzlePlayer key={`${sessionKey}-${puzzle.id}`} puzzle={puzzle} onCompleted={handlePuzzleCompleted} />
        <div className="actions">
          <button
            type="button"
            className="link-btn"
            onClick={() => setSessionIndex((i) => Math.max(0, i - 1))}
            disabled={sessionIndex === 0}
          >
            ◀ Précédent
          </button>
          <button
            type="button"
            className="primary-btn"
            onClick={() => (isLast ? setSession(null) : setSessionIndex((i) => i + 1))}
          >
            {isLast ? 'Terminer la série' : 'Puzzle suivant ▶'}
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="panel">
      <h2>Puzzles</h2>
      <p className="muted">
        Banque hors-ligne de {pool?.length ?? '…'} puzzles (base Lichess, licence CC0) — aucune requête réseau, tout
        est déjà dans l'application.
      </p>

      <div className="stat-summary">
        <div className="stat-tile">
          <strong>{rating}</strong>
          <span>niveau estimé</span>
        </div>
        <div className="stat-tile">
          <strong>{attempts.length}</strong>
          <span>tentative{attempts.length > 1 ? 's' : ''}</span>
        </div>
      </div>

      {themeStats.length > 0 && (
        <>
          <h3>Par thème</h3>
          <div className="chesscom-timeclass-chips">
            {themeStats.map((s) => (
              <span key={s.theme} className="chesscom-chip">
                {puzzleThemeLabel(s.theme)} — {s.solved}/{s.total}
              </span>
            ))}
          </div>
        </>
      )}

      <div className="actions" style={{ marginTop: '1.25rem' }}>
        {suggestedPuzzleThemes.length > 0 && primaryWeakness && (
          <button type="button" className="primary-btn" onClick={() => startSession(suggestedPuzzleThemes)} disabled={!pool}>
            Suggérés pour toi ({primaryWeakness.replace(/_/g, ' ')})
          </button>
        )}
        <button type="button" className="file-btn" onClick={() => startSession()} disabled={!pool}>
          Série de 5 (tous thèmes)
        </button>
      </div>
    </section>
  );
}
